// ./src/hooks/useAccount.ts
import { useState, useCallback, useMemo, useEffect } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  Account,
  AccountInput,
  AccountFilter,
  AccountService,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";

export interface UseAccountOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<AccountFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;
}

export interface UseAccountReturn {
  // Data state
  account: Account | null;
  accounts: Account[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<AccountFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isUpdating: boolean;
  selectedAccount: Account | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<AccountFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<Account[]>;
  update: (id: string, params: AccountInput) => Promise<Account | null>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<AccountFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedAccount: (account: Account | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  displayName: string | null;
  isActive: boolean;
  accountsByStatus: Record<string, Account[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useAccounts = (
  options: UseAccountOptions = {}
): UseAccountReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  // Data state
  const [account, setAccount] = useState<Account | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<AccountFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => accounts.length === 0 && !loading,
    [accounts.length, loading]
  );

  const displayName = useMemo(() => {
    if (!account) return null;
    return account.name || account.id || "Unknown Account";
  }, [account?.name, account?.id]);

  const isActive = useMemo(() => {
    return account?.status === "active";
  }, [account?.status]);

  const accountsByStatus = useMemo(() => {
    return accounts.reduce((acc, account) => {
      const status = account.status || "unknown";
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(account);
      return acc;
    }, {} as Record<string, Account[]>);
  }, [accounts]);

  // Helper function to update account in list if it exists, or add it if not
  const updateOrAddAccountInList = useCallback((updatedAccount: Account) => {
    setAccounts((prev) => {
      const existingIndex = prev.findIndex((a) => a.id === updatedAccount.id);
      if (existingIndex >= 0) {
        // Update existing account
        const newAccounts = [...prev];
        newAccounts[existingIndex] = updatedAccount;
        return newAccounts;
      } else {
        // Add new account
        return [updatedAccount, ...prev];
      }
    });
  }, []);

  // Helper function to remove account from list if it exists
  // const removeAccountFromList = useCallback((accountId: string) => {
  //   setAccounts((prev) => prev.filter((a) => a.id !== accountId));
  // }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setAccount(null);
    setAccounts([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsUpdating(false);
    setSelectedAccount(null);
  }, []);

  // Get single account (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await AccountService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const accountData = result.data[0] as Account;
          setAccount(accountData);

          // Update or add account in the list
          updateOrAddAccountInList(accountData);

          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load account";
          setLastError(errorMessage);
          setAccount(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load account";
        setLastError(errorMessage);
        setAccount(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddAccountInList]
  );

  // List accounts (matching API method name)
  const list = useCallback(
    async (
      filter: Partial<AccountFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSize;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setAccounts([]);
          setCurrentPage(1);
        }
      }

      // Update internal state
      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = (sortByParams || sortBy).map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await AccountService.list(
          filter as AccountFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newAccounts = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setAccounts((prev) => {
              const existingIds = new Set(prev.map((a) => a.id));
              const uniqueNewAccounts = newAccounts.filter(
                (a) => !existingIds.has(a.id)
              );
              return [...prev, ...uniqueNewAccounts];
            });
          } else {
            setAccounts(newAccounts);
          }

          setHasMore(newAccounts.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load accounts";
          setLastError(errorMessage);
          if (!isAppending) setAccounts([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load accounts";
        setLastError(errorMessage);
        if (!isAppending) setAccounts([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [pageSize, sortBy]
  );

  // Get many accounts by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<Account[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await AccountService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedAccounts = Array.isArray(result.data) ? result.data : [];

          // Update or add each loaded account to the list
          loadedAccounts.forEach((account) => {
            updateOrAddAccountInList(account);
          });

          setLastError(null);
          return loadedAccounts;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load accounts";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load accounts";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddAccountInList]
  );

  // Update account (matching API method name)
  const update = useCallback(
    async (id: string, params: AccountInput): Promise<Account | null> => {
      if (!id) {
        setLastError("Account ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await AccountService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedAccount = result.data[0] as Account;

          // Update single account if it matches
          if (account?.id === updatedAccount.id) {
            setAccount(updatedAccount);
          }

          // Update account in list if it exists
          updateOrAddAccountInList(updatedAccount);

          setLastError(null);
          return updatedAccount;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update account";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update account";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [account?.id, updateOrAddAccountInList]
  );

  // Load more accounts
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(filters, { page: nextPage, pageSize }, sortBy, false);
    }
  }, [list, loadingMore, hasMore, currentPage, pageSize, filters, sortBy]);

  // Refresh accounts
  const refresh = useCallback(async () => {
    await list(filters, { page: 1, pageSize }, sortBy, true);
  }, [list, filters, pageSize, sortBy]);

  // Auto-load effect - load data on mount if autoLoad is enabled
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      // Use initial filters and sort for auto-load
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, hasAutoLoaded, pageSize, initialFilters, initialSort, list]);

  return {
    // Data state
    account,
    accounts,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    filters,
    sortBy,

    // Pagination
    currentPage,
    pageSize,

    // Operations state
    isUpdating,
    selectedAccount,

    // Core operations (matching API method names exactly)
    get,
    list,
    getMany,
    update,

    // Additional convenience methods
    loadMore,
    refresh,

    // Filter operations
    setFilters,
    setSortBy,

    // UI state management
    setSelectedAccount,

    // Helper operations
    reset,

    // Computed values
    displayName,
    isActive,
    accountsByStatus,

    // Error handling
    lastError,
    clearError,
  };
};
